import React, { useEffect, useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveLink } from '../../features/sidebar/sidebarSlice';
import LoadingSpinner from '../../components/layout/LoadingSpinner';
import SignOutModal from '../signout/SignOutModal';
import { logout } from '../../features/auth/authSlice';
import SideNavigationBar from './SideNavigationbar';
import OnboardingCarousel from '../common/onboarding/OnboardingCarousel';
import { RootState } from '../../store';
import { APP_CONFIG } from '../../config';
import { setSideBarConfig } from '../../features/sideBarConfigSlice/sideBarConfigSlice';
import { useGetSideBarConfigQuery } from '../../api/sideBarConfig';
import {
    setSchoolId,
    setSchoolInfo,
} from '../../features/schoolInfo/schoolInfoSlice';
import { useGetSchoolInfoQuery } from '../../api/schoolInfo';
import { ProductType } from '../product-type-context/ProductTypeContext';
import PendingActionsModal from '../pending-actions/pending-actions-modal';
import { useGetStatusQuery } from '../../api/pending-card';
import { setTeacherPendingActionsWidgetOpen } from '../../features/techerPendingActionsWidgetModal/teacherPendingActionsWidgetModalSlice';
import MediaPlayerHandler from '../players/MediaPlayerHandler';
import {
    ACTIVE_TEACH_ONBOARDING_VIDEO_LINK,
    BUILDR_ONBOARDING_VIDEO_LINK,
} from '../../constants';
import CloseButtonIcon from '../../assets/images/closeIcon.svg';
import { trackEvent } from '../../segment';
import { notification } from 'antd';

interface SideMenuSubItem {
    key: string;
    displayName: string;
    visibleToRoles: string[];
    visibleToProducts?: string[];
    availability: 'online' | 'offline' | 'both';
    icon?: string;
    onClickAction?: string;
    activeIcon?: string;
    inactiveIcon?: string;
}
interface SideMenuItem {
    key: string;
    displayName: string;
    visibleToRoles: string[];
    visibleToProducts?: string[];
    availability: 'online' | 'offline' | 'both';
    icon?: string;
    subItems: SideMenuSubItem[];
}

interface SideBarConfigState {
    success: boolean;
    data: {
        sideNavItems: SideMenuItem[];
    };
}
interface SchoolInfoConfig {
    success: boolean;
    data: {
        product: string;
        school: { id: number; name: string };
        academicyearid: any;
        boardId: any;
    };
}

const NavigationLayout: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.auth.user);
    const school_id = user?.su[0]?.schoolid || user?.su[0]?.id || '';
    const nucleusDomain = process.env
        .REACT_APP_NUCLEUS_ACTIVE_TEACH_URL as string;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [widgetStatus, setWidgetStatus] = useState(false);

    const isResourceViewerOpen = useSelector(
        (state: RootState) => state.ResourceViewer.isOpen
    );
    const isPendingActionsModalOpen = useSelector(
        (state: { TeacherPendingActionsWidgetOpen: { isOpen: boolean } }) =>
            state.TeacherPendingActionsWidgetOpen.isOpen
    );
    const setIsPendingActionsModalOpen = (value: boolean | null) => {
        if (value === null) {
            dispatch(
                setTeacherPendingActionsWidgetOpen(!isPendingActionsModalOpen)
            );
        } else {
            dispatch(setTeacherPendingActionsWidgetOpen(value));
        }
    };
    const product = useSelector(
        (state: RootState) => state.product
    ) as ProductType;
    const productType = useSelector(
        (state: RootState) => state.SchoolInfo.data.product
    );
    const [isLoading, setIsLoading] = useState(false);
    const { data: school_info_res, error: school_info_error } =
        useGetSchoolInfoQuery(
            { product, school_id },
            { skip: !product }
        ) as any;
    const academicyearid = useSelector(
        (state: RootState) => state.SchoolInfo.data.academicyearid
    );
    const userData = localStorage.getItem('user');
    const lastLoginTimestamp = userData
        ? JSON.parse(userData)?.lastLoginTimestamp
        : null;
    const [transpeentModalOpen, setTransparentModalOpen] = useState(true);
    const {
        data: getStatusData,
        isLoading: isGetStatusLoading,
        refetch: getStatusRefetch,
    } = useGetStatusQuery(
        {
            schoolId: school_id.toString(),
            acadId: academicyearid || '',
            clientApp: product === 'Buildr' ? 'BUILDR' : product,
        },
        {
            skip:
                process.env.REACT_APP_INSTALLATION_MODE !== 'webapp' ||
                process.env.REACT_APP_APPLICATION_NAME !== 'Buildr' ||
                product !== 'Buildr' ||
                !school_id ||
                !academicyearid ||
                ![
                    'ADM',
                    'SCH41',
                    'SCH31',
                    'EXC31',
                    'EXC21',
                    'CS21',
                    'CS31',
                    'CS11',
                ].includes(user?.rolecode || ''),
        }
    );

    window.addEventListener('message', (event) => {
        if (event && event.data) {
            const receivedData = event.data;
            if (
                receivedData.type &&
                receivedData.type === 'ACTIVE_TEACH_STUDENT' &&
                typeof receivedData.showMark === 'boolean' &&
                receivedData.showMark === true
            ) {
                getStatusRefetch();
            }
        }
    });

    if (school_info_res?.success) {
        localStorage.setItem(
            'INSTALLATION_PACKAGE',
            school_info_res.data.installation_package?.INSTALLATION_MODE
        );
        dispatch(setSchoolInfo(school_info_res));
        if (school_id) {
            dispatch(setSchoolId(school_id));
        }
    }
    const {
        data: res,
        isError,
        refetch,
    } = useGetSideBarConfigQuery(
        product === 'Buildr'
            ? 'Buildr'
            : school_info_res?.data.product
                ? school_info_res?.data.product
                : 'ActiveTeach',
        {
            skip: !product,
        }
    ) as {
        data: SideBarConfigState | null;
        isError: boolean;
        refetch: Function;
    };

    if (res?.success) {
        dispatch(setSideBarConfig(res));
    }

    useEffect(() => {
        dispatch(setActiveLink(location.pathname));
    }, [location.pathname, dispatch]);

    const pendingActionsCount = useMemo(() => {
        if (
            !isLoading &&
            process.env.REACT_APP_INSTALLATION_MODE === 'webapp'
        ) {
            if (getStatusData && getStatusData.data) {
                return (
                    getStatusData.data.totalSections -
                    getStatusData.data.sectionsCompleted
                );
            }
        }
        return 0;
    }, [isLoading, getStatusData]);

    // const callRemoveLocal = () => {
    //     localStorage.clear();
    //     sessionStorage.clear();
    //     // Create an invisible iframe to load the other domain
    //     const iframe = document.createElement('iframe');
    //     iframe.src = nucleusDomain;
    //     iframe.style.display = 'none'; // Hide the iframe
    //     document.body.appendChild(iframe);

    //     // Send a message after the iframe loads
    //     iframe.onload = () => {
    //         iframe?.contentWindow?.postMessage(
    //             { action: 'clearLocalStorage' },
    //             nucleusDomain
    //         );
    //     };

    //     // Remove the iframe after a delay
    //     setTimeout(() => {
    //         document.body.removeChild(iframe);
    //         navigate('/login?logout=true',
    //             {
    //                 state: { logout: true },
    //                 replace: true
    //             });
    //     }, 3000);
    // };

    const handleLogout = () => {
        // Backup nudgeData
        const nudgeData = localStorage.getItem('nudgeData');

        const iframe = document.createElement('iframe');
        if (iframe) {
            iframe.contentWindow?.postMessage(
                { type: 'LOGOUT' },
                nucleusDomain
            );
        }

        localStorage.clear();
        sessionStorage.clear();

        // Restore nudgeData
        if (nudgeData) {
            localStorage.setItem('nudgeData', nudgeData);
        }

        navigate('/login', {
            state: { logout: true },
            replace: true,
        });
    };

    useEffect(() => {
        if(getStatusData?.success && getStatusData.data?.msg && getStatusData.data?.msg !== '') {
            notification.error({
                message: 'Error',
                description: getStatusData.data.msg,
                duration: 3,
            });
            setWidgetStatus(true);
        }
        return () => {
            setWidgetStatus(false);
        }
    }, [getStatusData])

    const handleSignOut = () => {
        //old event
        trackEvent('side_menu_sign_out_clicked', {});
        trackEvent('click_signout', {});
        dispatch(logout()); // Clear Redux state
        setIsModalOpen(false);
        handleLogout();
    };

    return (
        <div className="flex flex-col min-h-screen font-sans text-[#394D8B]">
            <div className={`flex flex-1 font-sans font-semibold relative`}>
                {!isResourceViewerOpen && (
                    <SideNavigationBar
                        isPendingActionsModalOpen={isPendingActionsModalOpen}
                        setIsPendingActionsModalOpen={
                            setIsPendingActionsModalOpen
                        }
                        setIsModalOpen={setIsModalOpen}
                        pendingActionsCount={pendingActionsCount}
                        setCollapsed={setCollapsed}
                        collapsed={collapsed}
                        isGetStatusLoading={isGetStatusLoading}
                        getStatusData={getStatusData}
                        widgetStatus={widgetStatus}
                        schoolId={school_id}
                        dealId={school_info_res?.data?.school?.dealId || ''}
                    />
                )}
                <div className="flex-grow transition duration-1000 ease-in-out">
                    <main
                        className={`${!isResourceViewerOpen ? 'ml-[4.375rem] bg-[#F5F7FA]' : 'z-0 bg-gray-100'} content-height`}
                    >
                        {school_info_res && <Outlet />}
                    </main>
                </div>
            </div>

            {/*Onboarding Video Modal */}
            {!lastLoginTimestamp &&
                process.env.REACT_APP_INSTALLATION_MODE === 'webapp' &&
                transpeentModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                        <div className="relative w-full max-w-4xl h-auto bg-white rounded-lg overflow-hidden">
                            <button
                                onClick={() => setTransparentModalOpen(false)}
                                className="absolute top-2 right-2 z-10 text-white rounded-full p-2"
                            >
                                <img src={CloseButtonIcon} alt="Close button" />
                            </button>
                            <MediaPlayerHandler
                                type={'video'}
                                resId={''}
                                completeUrl={
                                    product == 'Buildr'
                                        ? BUILDR_ONBOARDING_VIDEO_LINK
                                        : ACTIVE_TEACH_ONBOARDING_VIDEO_LINK
                                }
                            />
                        </div>
                    </div>
                )}
            {isModalOpen && (
                <SignOutModal
                    onClose={() => setIsModalOpen(false)}
                    onSignOut={handleSignOut}
                />
            )}
            {showOnboarding && (
                <OnboardingCarousel onClose={() => setShowOnboarding(false)} />
            )}
            {product === 'Buildr' && isPendingActionsModalOpen && (
                <PendingActionsModal
                    collapsed={collapsed}
                    sections={
                        Object.values(getStatusData?.data?.sections || {}) || []
                    }
                    isLoading={isGetStatusLoading}
                    pendingActionsCount={pendingActionsCount || 0}
                    setIsPendingActionsModalOpen={setIsPendingActionsModalOpen}
                />
            )}

            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};

export default NavigationLayout;
